import React, { useState } from 'react';
import { MdNotifications, MdSend, MdEdit, MdClose, MdCheckCircle, MdCancel, MdExpandMore } from 'react-icons/md';
import { formatLocalDateLabel } from '../utils/notificationRecipient';

/**
 * Komponent wyświetlający kolejkę powiadomień w prawym dolnym rogu.
 * Domyślnie zwinięty (tylko liczba); rozwija się w górę po kliknięciu.
 */
export default function NotificationQueue({
    queue,
    isVisible,
    onSend,
    onEdit,
    onReject,
    onClose,
    selectedCount,
    queueCount,
    isSending = false,
    unmappedCount = 0,
}) {
    const [expanded, setExpanded] = useState(false);

    if (!isVisible || queueCount === 0) {
        return null;
    }

    const getNotificationText = (notification) => {
        const { type, eventName, date, staffName } = notification;
        const dateStr = formatLocalDateLabel(date);

        switch (type) {
            case 'add_staff':
                return `Dodano: ${staffName} → ${eventName} (${dateStr})`;
            case 'remove_staff':
                return `Usunięto: ${staffName} z ${eventName} (${dateStr})`;
            case 'propose_staff':
                return `Propozycja: ${staffName} → ${eventName} (${dateStr})`;
            default:
                return `${staffName} - ${eventName} (${dateStr})`;
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'add_staff':
                return <MdCheckCircle className="text-green-500" />;
            case 'remove_staff':
                return <MdCancel className="text-red-500" />;
            case 'propose_staff':
                return <MdNotifications className="text-yellow-500" />;
            default:
                return <MdNotifications />;
        }
    };

    const label = queueCount === 1
        ? '1 powiadomienie do wysłania'
        : `${queueCount} powiadomień do wysłania`;

    return (
        <div
            className="fixed bottom-4 right-4 z-[9999] w-full max-w-[380px]
                     bg-white dark:bg-gray-800
                     border border-gray-200 dark:border-gray-700
                     rounded-t-lg shadow-lg overflow-hidden
                     flex flex-col"
        >
            {/* Zwinięty pasek – zawsze widoczny gdy jest kolejka */}
            <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3
                         text-left hover:bg-gray-50 dark:hover:bg-gray-700/50
                         border-b border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <MdNotifications className="text-blue-500 shrink-0" size={20} />
                    <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {label}
                    </span>
                </div>
                <span
                    className={`shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
                    aria-hidden
                >
                    <MdExpandMore size={24} className="text-gray-500" />
                </span>
            </button>

            {/* Rozwinięta zawartość – wysuwa się w górę */}
            <div
                className={`grid transition-[grid-template-rows] duration-200 ease-out
                    ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
                <div className="min-h-0 overflow-hidden">
                    <div className="px-4 py-3 space-y-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Zaznaczono: {selectedCount}/{queueCount}
                            </span>
                            <button
                                type="button"
                                onClick={onClose}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                title="Ukryj kolejkę"
                            >
                                <MdClose size={20} />
                            </button>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={onSend}
                                disabled={selectedCount === 0 || isSending}
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600
                                         disabled:bg-gray-300 disabled:cursor-not-allowed
                                         dark:bg-blue-600 dark:hover:bg-blue-700
                                         flex items-center gap-2 text-sm"
                            >
                                <MdSend size={18} />
                                {isSending ? 'Wysyłanie…' : `Wyślij (${selectedCount})`}
                            </button>
                            <button
                                type="button"
                                onClick={onEdit}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300
                                         dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600
                                         flex items-center gap-2 text-sm"
                            >
                                <MdEdit size={18} />
                                Edytuj
                            </button>
                            <button
                                type="button"
                                onClick={() => { onReject(); setExpanded(false); }}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600
                                         dark:bg-red-600 dark:hover:bg-red-700
                                         flex items-center gap-2 text-sm"
                            >
                                <MdClose size={18} />
                                Nie wysyłaj
                            </button>
                        </div>

                        {unmappedCount > 0 && (
                            <div className="text-sm text-amber-700 dark:text-amber-400">
                                {unmappedCount === 1
                                    ? '1 osoba bez konta — nie wyśle się, dopóki nie będzie w liście pracowników.'
                                    : `${unmappedCount} osób bez konta — nie wyślą się, dopóki nie będą w liście pracowników.`}
                            </div>
                        )}
                        {queue.length > 0 && (
                            <div className="text-sm text-gray-600 dark:text-gray-400 max-h-32 overflow-y-auto">
                                {queue.slice(0, 3).map((notif) => (
                                    <div key={notif.id} className="flex items-center gap-2 py-1">
                                        {getNotificationIcon(notif.type)}
                                        <span className="truncate">{getNotificationText(notif)}</span>
                                        {notif.mappingError && (
                                            <span className="shrink-0 text-amber-700 dark:text-amber-400">brak konta</span>
                                        )}
                                    </div>
                                ))}
                                {queue.length > 3 && (
                                    <div className="text-gray-500 dark:text-gray-500">
                                        ... i {queue.length - 3} więcej
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
